import type { ScaleLinear, ScaleTime } from "d3-scale";
import { createContext, useContext } from "react";
import type { BaseDataPoint } from "../utils/charts";
import type { ComponentFormatter } from "../utils/formatting";

/**
 * Chart Y-axis formatter type
 * Uses the main formatting system for consistency with Kpi, StatList, and DataTable
 */
export type YFormatType = ComponentFormatter;

export interface TooltipData {
  /** The x-axis value (Date for time-series charts) */
  xValue: Date;
  series: TooltipSeries[];
  x: number;
  y: number;
  position: "left" | "right";
  /** Whether this tooltip is for an event marker (vs data point) */
  isEvent?: boolean;
  /** Optional event metadata to display */
  eventMetadata?: Record<string, string | number>;
}

export interface TooltipSeries {
  label: string;
  value: number;
  color: string;
  type?: "area" | "line" | "bar" | "event";
  category?: string;
  categoryColor?: string;
}

export interface SeriesMetadata {
  label: string;
  color: string;
}

interface ChartContextType {
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  width: number;
  height: number;
  yFormatter: YFormatType;
  animationSettings: {
    duration: number;
    ease: string;
  };
  tooltip: {
    data: TooltipData | null;
    show: (data: TooltipData) => void;
    hide: () => void;
  };
  /** Series datasets for accessing underlying data */
  datasets?: BaseDataPoint[][];
  /** Series metadata for labels and colors */
  seriesMetadata?: SeriesMetadata[];
  /** Series types for proper tooltip rendering */
  seriesTypes?: ("area" | "line" | "bar")[];
  /** Category colors for bar charts */
  categoryColors?: Record<string, string>;
  /** Category labels for translating category values */
  categoryLabels?: Record<string, string>;
}

export const defaultAnimationSettings = {
  duration: 0.5,
  ease: "easeInOut",
};

export const ChartContext = createContext<ChartContextType | null>(null);

export type { ChartContextType };

export const useChartContext = () => {
  const context = useContext(ChartContext);
  if (!context) {
    throw new Error("useChartContext must be used within a ChartContextProvider");
  }
  return context;
};
